import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';

function ResetPassword() {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [token, setToken] = useState('');
    const [passwordReset, setPasswordReset] = useState(false);

    useEffect(() => {
        // Extract token from URL query parameters
        const urlParams = new URLSearchParams(location.search);
        const resetToken = urlParams.get('token');
        if (resetToken) {
            setToken(resetToken);
        } else {
            toast.error(t('resetPassword.errors.invalidLink'));
            navigate('/login');
        }
    }, [location, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!password) {
            toast.error(t('resetPassword.errors.passwordRequired'));
            return;
        }

        if (password.length < 6) {
            toast.error(t('resetPassword.errors.passwordMinLength'));
            return;
        }

        if (password !== confirmPassword) {
            toast.error(t('resetPassword.errors.passwordsMismatch'));
            return;
        }

        if (!token) {
            toast.error(t('resetPassword.errors.invalidToken'));
            return;
        }

        setLoading(true);
        try {
            await apiService.resetPassword(token, password);
            setPasswordReset(true);
            toast.success(t('resetPassword.messages.successToast'));
        } catch (error) {
            if (error.response?.status === 400) {
                toast.error(t('resetPassword.errors.invalidOrExpired'));
            } else {
                toast.error(t('resetPassword.errors.generic'));
            }
        } finally {
            setLoading(false);
        }
    };

    if (passwordReset) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full space-y-8">
                    <div>
                        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                            {t('resetPassword.success.title')}
                        </h2>
                        <p className="mt-2 text-center text-sm text-gray-600">
                            {t('resetPassword.success.subtitle')}
                        </p>
                    </div>

                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="text-center">
                            <svg className="mx-auto h-12 w-12 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                            <h3 className="mt-2 text-lg font-medium text-gray-900">{t('resetPassword.success.allSet')}</h3>
                            <p className="mt-2 text-sm text-gray-500">
                                {t('resetPassword.success.description')}
                            </p>
                            <div className="mt-6">
                                <Link
                                    to="/login"
                                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    {t('resetPassword.success.cta')}
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        {t('resetPassword.form.title')}
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        {t('resetPassword.form.subtitle')}
                    </p>
                </div>
                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                {t('resetPassword.form.newPasswordLabel')}
                            </label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                autoComplete="new-password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                                placeholder={t('resetPassword.form.newPasswordPlaceholder')}
                                minLength="6"
                            />
                        </div>

                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                                {t('resetPassword.form.confirmPasswordLabel')}
                            </label>
                            <input
                                id="confirmPassword"
                                name="confirmPassword"
                                type="password"
                                autoComplete="new-password"
                                required
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                                placeholder={t('resetPassword.form.confirmPasswordPlaceholder')}
                                minLength="6"
                            />
                        </div>
                    </div>

                    <div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : null}
                            {t('resetPassword.form.submit')}
                        </button>
                    </div>

                    <div className="text-center">
                        <Link
                            to="/login"
                            className="font-medium text-blue-600 hover:text-blue-500"
                        >
                            {t('resetPassword.form.backToLogin')}
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default ResetPassword;
